import axios, { type AxiosError, type AxiosRequestConfig } from 'axios'
import type { z } from 'zod'
import { type ApiErrorResponse, AppError } from '../handle-api-error'
import { useAuthStore } from '../stores/auth-store'

export interface ApiConfig {
  baseURL: string
  timeout?: number
  refreshTokenRoute: string
  onLogout?: () => void
}

const HTTP_STATUS_UNAUTHORIZED = 401

export function createApi(config: ApiConfig) {
  const { baseURL, timeout = 10000, refreshTokenRoute, onLogout } = config

  const instance = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  instance.interceptors.request.use((config) => {
    const token = useAuthStore.getState().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  let isRefreshing = false
  let failedQueue: Array<{
    resolve: (token: string) => void
    reject: (error: unknown) => void
  }> = []

  const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error)
      } else if (token) {
        prom.resolve(token)
      }
    })
    failedQueue = []
  }

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

      if (error.response?.status === HTTP_STATUS_UNAUTHORIZED && originalRequest) {
        if (originalRequest.url?.includes(refreshTokenRoute)) {
          useAuthStore.getState().logout()
          onLogout?.()
          return Promise.reject(error)
        }

        if (!isRefreshing) {
          isRefreshing = true

          try {
            const refreshToken = useAuthStore.getState().refreshToken

            if (!refreshToken) {
              throw new Error('No refresh token available')
            }

            const response = await axios.post(
              `${baseURL}${refreshTokenRoute}`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${refreshToken}`,
                },
              },
            )

            const { data } = response.data as { data: { access_token: string } }

            const user = useAuthStore.getState().user
            if (user) {
              useAuthStore.getState().setAuth(user, data.access_token, refreshToken)
            }

            instance.defaults.headers.common.Authorization = `Bearer ${data.access_token}`
            processQueue(null, data.access_token)

            return instance(originalRequest)
          } catch (refreshError) {
            processQueue(refreshError, null)
            useAuthStore.getState().logout()
            onLogout?.()
            return Promise.reject(refreshError)
          } finally {
            isRefreshing = false
          }
        }

        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              resolve(instance(originalRequest))
            },
            reject: (err: unknown) => {
              reject(err)
            },
          })
        })
      }

      if (
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'code' in error.response.data
      ) {
        return Promise.reject(new AppError(error.response.data as ApiErrorResponse))
      }

      return Promise.reject(error)
    },
  )

  async function request<T>(config: AxiosRequestConfig & { schema: z.ZodSchema<T> }): Promise<T> {
    const { schema, ...axiosConfig } = config
    const response = await instance(axiosConfig)
    return schema.parse(response.data)
  }

  return {
    api: instance,
    request,
  }
}
